import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {Image} from '@jobseeker/lotus/components/Image';
import {ChevronRight} from '@jobseeker/lotus/icons';

const LINKEDIN_BLUE = '#0A66C2';

export interface LinkedInSyncTileProps {
  onClick: () => void;
  borderColor?: string;
  chevronSide?: 'left' | 'right';
  size?: 'compact' | 'large';
}

const TILE_SIZES = {
  compact: {
    gap: '9px',
    p: '10px 10px',
    icon: 20,
    iconRadius: '5px',
    iconFont: 10,
    title: 13,
    subtitle: 10,
    chevron: 10,
  },
  large: {
    gap: '12px',
    p: '12px 14px',
    icon: 28,
    iconRadius: '6px',
    iconFont: 13,
    title: 15,
    subtitle: 12,
    chevron: 12,
  },
} as const;

const LinkedInSyncTile = ({
  onClick,
  borderColor = '#49966E',
  chevronSide = 'right',
  size = 'compact',
}: LinkedInSyncTileProps) => {
  const dims = TILE_SIZES[size];
  const chevron = (
    <Box sx={{flex: 'none', display: 'flex', alignItems: 'center'}}>
      <Image
        src={ChevronRight}
        alt={'Arrow'}
        width={dims.chevron}
        height={dims.chevron}
        objectFit="cover"
        style={
          chevronSide === 'left' ? {transform: 'rotate(180deg)'} : undefined
        }
      />
    </Box>
  );

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      data-testid="jobfeed-linkedin-sync-tile"
      data-component="LinkedInSyncTile"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: dims.gap,
        width: '100%',
        mt: '14px',
        p: dims.p,
        border: `1px solid ${borderColor}`,
        borderRadius: size === 'large' ? '10px' : '8px',
        bgcolor: 'transparent',
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        '&:focus-visible': {
          outline: `2px solid ${LINKEDIN_BLUE}`,
          outlineOffset: '2px',
        },
      }}
    >
      {chevronSide === 'left' && chevron}
      <Box
        aria-hidden="true"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 'none',
          width: dims.icon,
          height: dims.icon,
          borderRadius: dims.iconRadius,
          bgcolor: LINKEDIN_BLUE,
          color: '#fff',
          fontSize: dims.iconFont,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        in
      </Box>

      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography
          sx={{
            fontSize: dims.title,
            fontWeight: 600,
            color: '#22303A',
            lineHeight: 1.55,
          }}
        >
          Sync with LinkedIn
        </Typography>
        <Typography
          sx={{
            fontSize: dims.subtitle,
            color: '#7A868E',
            lineHeight: 1.35,
            mt: '1px',
          }}
        >
          Never update your profile manually again
        </Typography>
      </Box>

      {chevronSide === 'right' && chevron}
    </Box>
  );
};

export default LinkedInSyncTile;
